// @flow

import * as React from 'react';

import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';

import { useLocation } from 'react-router-dom';

import {
  selectCartTotal,
  selectCartCurrency,
  selectContracts,
  selectProductErrors,
} from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Svg } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { selectIsAppWorking } from 'src/modules/app/selectors';

import { CheckoutButton } from './CheckoutButton';
import { ContinueButton } from './ContinueButton';

import { GenerateProformaButton } from './GenerateProformaButon';
import { PayLaterLink } from './PayLaterLink';

import styles from './CartSummaryButton.scss';

type Props = {
  scrollBottom: boolean,
};

type RefType = HTMLDivElement;

function Button({ price }: { price: string }) {
  const location = useLocation();
  const overviewPage = location.pathname.includes('overview');
  const generateProformaPage = location.pathname.includes('generate-proforma');

  switch (true) {
    case generateProformaPage:
      return <GenerateProformaButton price={price} />;
    case overviewPage:
      return <CheckoutButton price={price} />;
    default:
      return <ContinueButton price={price} />;
  }
}

function CartSummaryButton(
  { scrollBottom }: Props,
  ref: React.RefSetter<RefType | null>,
): React.MixedElement | null {
  const translator = useTranslator();

  const { __, formatPrice } = translator;

  const location = useLocation();
  const overviewPage = location.pathname.includes('overview');

  const currency = useAppSelector(selectCartCurrency);
  const total = useAppSelector(selectCartTotal);

  const contracts = useAppSelector(selectContracts);
  const allContractsApproved = contracts.every(contract => contract.isApproved);

  const errors = useAppSelector(selectProductErrors);
  const hasErrors = errors?.length > 0;

  const isAppWorking = useAppSelector(selectIsAppWorking);

  if (isEmpty(total) || !currency) return null;
  const { afterTaxes } = total;

  const price = formatPrice(afterTaxes, currency);
  return (
    <div
      ref={ref}
      id="cartSummaryButton"
      className={clsx(
        styles.container,
        scrollBottom ? styles.scrollBottom : undefined,
      )}
    >
      {overviewPage &&
        (allContractsApproved ? (
          hasErrors ? (
            <p className={styles.information}>
              <Svg id="error" name="warningWire" />
              {__(
                '<for-translators-use>%(count)s</for-translators-use>Some products in your cart require your attention.',
                { count: errors.length },
              )}
            </p>
          ) : (
            !isAppWorking && <PayLaterLink />
          )
        ) : (
          <p className={styles.information}>
            <Svg id="warningWire" name="warningWire" />
            {__(
              '<for-translators-use>%(count)s</for-translators-use>Please accept all contracts above to proceed.',
              {
                count: contracts.length,
              },
            )}
          </p>
        ))}

      <Button price={price} />
    </div>
  );
}

const CartSummaryButtonForwarded: React.ComponentType<Props> = React.forwardRef<
  Props,
  RefType | null,
>(CartSummaryButton);

export default CartSummaryButtonForwarded;
